import { asyncTimeout } from "@use/async-timeout";

import { CourseCollection } from "@courses-models/course";
import {
  InstitutionClassCollection,
  InstitutionTeacherCollection,
} from "@courses-models/institution";
import { UnicUserModel, UserTypes } from "@courses-models/user";
import {
  AuthenticatedUserModel,
  authenticateUser,
  redirectToLoginConnector,
} from "@courses-utils/gateway-auth";

import { loginConnector } from "@apps/mf-overview/src/ts/config";
import { Store } from "@apps/mf-overview/src/ts/stores/Store";
import { getUserType } from "@apps/mf-overview/src/ts/utils/getUserType";

/**
 * Cache used to prevent re-creating a store over and over again.
 */
let cache: Promise<Store>;
/**
 * Helper that'll create a new instance of the Store by fetching data about
 * the currently authenticated user.
 */
export const createStore = async () => {
  if (!cache) {
    cache = new Promise<Store>(async (resolve, reject) => {
      resolve(new Store());
    });
  }

  return cache;
};

/**
 * Helper that'll fetch a list of courses available for the currently signed in
 * user, based on this profile type.
 */
async function fetchCourses(
  user: AuthenticatedUserModel,
): Promise<CourseCollection> {
  return getUserType(user) === UserTypes.TEACHER
    ? CourseCollection.GET_BY_TEACHER_ID(user.id)
    : CourseCollection.GET_BY_STUDENT_ID(user.id);
}

/**
 * Helper that'll fetch a list of classes available on the institution of the
 * currently signed in user.
 */
async function fetchClasses(
  user: AuthenticatedUserModel,
): Promise<{ id: string; name: string }[]> {
  // If the user wasn't signed in with e-key, then bail out... There's no data
  // available for him anyway
  if (!(user instanceof UnicUserModel)) {
    return [];
  }

  // ... Otherwise fetch a collection of available classes
  return (await InstitutionClassCollection.GET_BY_ID(user.institutionId))
    .map((cls) => cls)
    .sort((a, b) => (a.name > b.name ? 1 : -1));
}

/**
 * Helper that'll fetch a list of co-workers available on the institution of the
 * currently signed in user.
 */
async function fetchCoWorkers(
  user: AuthenticatedUserModel,
): Promise<{ id: string; name: string }[]> {
  // If the user wasn't signed in with e-key, then bail out... There's no data
  // available for him anyway
  if (!(user instanceof UnicUserModel)) {
    return [];
  }

  // ... Otherwise fetch a collection of available classes
  return (await InstitutionTeacherCollection.GET_BY_ID(user.institutionId))
    .map((coWorker) => coWorker)
    .sort((a, b) => (a.name > b.name ? 1 : -1));
}

// /**
//  * Specification of response data for a single product in the productData
//  * endpoint.
//  */
// type ProductDataResponse__Product = {
//     licenseKey: string;
//     grade: string[];
//     subject: string[];
// };

// /**
//  * Specification of the response format from the productData endpoint.
//  */
// type ProductDataResponse = {
//     library: {
//         book: ProductDataResponse__Product;
//     }[];
//     products: {
//         portal?: {
//             product: ProductDataResponse__Product;
//         }[];
//         webtest?: {
//             product: ProductDataResponse__Product;
//         }[];
//         misc?: {
//             product: ProductDataResponse__Product;
//         }[];
//         ibog?: {
//             product: ProductDataResponse__Product;
//         }[];
//         ""?: {
//             product: ProductDataResponse__Product;
//         }[];
//     }[];
// };

// /**
//  * Helper that'll fetch data about all products that the currently signed in
//  * user has access to.
//  */
// async function fetchProductData(): Promise<{
//     isbn: string;
//     levels: string[];
//     subjects: string[];
// }[]> {
//     // Prepare collection of grades and subjects from the available products
//     const products: {
//         isbn: string;
//         levels: string[];
//         subjects: string[];
//     }[] = [];

//     // Fetch data
//     const response = await axios.get<ProductDataResponse>(api.productData);

//     // Iterate over all books in the output
//     for (const book of response.data.library) {
//         products.push({
//             isbn: book.book.licenseKey,
//             levels: book.book.grade,
//             subjects: book.book.subject,
//         });
//     }

//     // Iterate over all products in the output
//     for (const product of response.data.products) {
//         // Handle portals
//         if (product.portal) {
//             for (const portal of product.portal) {
//                 products.push({
//                     isbn: portal.product.licenseKey,
//                     levels: portal.product.grade,
//                     subjects: portal.product.subject,
//                 });
//             }
//         }

//         // Handle webtests
//         if (product.webtest) {
//             for (const webtest of product.webtest) {
//                 products.push({
//                     isbn: webtest.product.licenseKey,
//                     levels: webtest.product.grade,
//                     subjects: webtest.product.subject,
//                 });
//             }
//         }

//         // Handle ibog
//         if (product.ibog) {
//             for (const ibog of product.ibog) {
//                 products.push({
//                     isbn: ibog.product.licenseKey,
//                     levels: ibog.product.grade,
//                     subjects: ibog.product.subject,
//                 });
//             }
//         }

//         // Handle misc
//         if (product.misc) {
//             for (const misc of product.misc) {
//                 products.push({
//                     isbn: misc.product.licenseKey,
//                     levels: misc.product.grade,
//                     subjects: misc.product.subject,
//                 });
//             }
//         }

//         // Handle others
//         if (product[""]) {
//             // @ts-ignore
//             for (const misc of product[""]) {
//                 products.push({
//                     isbn: misc.product.licenseKey,
//                     levels: misc.product.grade,
//                     subjects: misc.product.subject,
//                 });
//             }
//         }
//     }

//     return products;
// }

// /**
//  * Helper that'll fetch a collection of products that's licensed for the user
//  * to access.
//  */
// async function fetchUserLicenses(user: AuthenticatedUserModel): Promise<LicenseCollection> {
//     return (
//         user instanceof UnicUserModel
//             ? LicenseCollection.GET_BY_UNIC_USER_ID(user.id)
//             : LicenseCollection.GET_BY_EKEY_USER_ID(user.ekeyId)
//     );
// }

// /**
//  * Helper that'll extract levels and subjects from the user's licensed products.
//  */
// async function fetchLevelsAndSubjects(user: AuthenticatedUserModel): Promise<{
//     levels: string[];
//     subjects: string[];
// }> {
//     // Fetch product data and licenses
//     const [ products, licenses ] = await Promise.all([
//         fetchProductData(),
//         fetchUserLicenses(user),
//     ]);

//     // Iterate over list of products, and determine which ones the user has an
//     // active license for
//     const licensedProducts = products.filter((product) => licenses.contains(product.isbn));

//     // Now iterate over licensed products, and extract unique list of available
//     // levels and subjects
//     const rawLevels = new Set<string>();
//     const rawSubjects = new Set<string>();

//     for (const product of licensedProducts) {
//         for (const level of product.levels) {
//             rawLevels.add(level);
//         }

//         for (const subject of product.subjects) {
//             rawSubjects.add(subject);
//         }
//     }

//     // Finally perform sorting of levels and subjects, so that they're easy for
//     // editors to review
//     const levels = Array.from(rawLevels)
//         .sort((a, b) => sortSafeLevel(a) >= sortSafeLevel(b) ? 1 : -1);

//     const subjects = Array.from(rawSubjects)
//         .sort();

//     // Return levels and subjects publicly
//     return { levels, subjects };
// }

// /**
//  * Helper that'll transform the given level into a sort-safe string, so that
//  * they will appear in a logical order for teachers.
//  */
// function sortSafeLevel(level: string): string {
//     return level
//         // Levels given as "[12]. [literal]" appears third in the list, allowing
//         // natural sorting of single and double digits
//         .replace(/^([0-9]{1,2})\./g, (str, match: string) => (
//             match.length < 2
//                 ? `20${ match }`
//                 : `2${ match}`
//         ))

//         // Levels given as "[12] [literal]" appears fourth in the list, allowing
//         // natural sorting of single and double digits
//         .replace(/^([0-9]{1,2})[^0-9\.]/g, (str, match: string) => (
//             match.length < 2
//                 ? `30${ match }`
//                 : `3${ match }`
//         ))

//         // Levels given as "[AB]-[literal]" appears fifth in the list, allowing
//         // natural sorting of single and double letters
//         .replace(/^([a-zA-Z]{1,2})\-/g, (str, match: string) => (
//             match.length < 2
//                 ? `4a${ match.toLowerCase() }`
//                 : `4${ match.toLowerCase() }`
//         ))

//         // "Dagtilbud" always appears first in the list
//         .replace(/^Dagtilbud$/i, "0Dagtilbud")

//         // "Børnehaveklasse" always appears second in the list
//         .replace(/^Børnehaveklasse$/i, "1Børnehaveklasse");
// }
